<template>
  <form class="form-banner w-100 h-100">
    <div class="thanks h-100 d-flex align-items-center justify-content-center" v-if="state.showThanksMessage">
      <div>
        <h2 class="text-secondary mb-5 text-center">{{successFormMessage?.title}}</h2>
        <div v-html="successFormMessage?.description" class="text-center"/>
      </div>
    </div>
    <div v-if="!state.showThanksMessage" class="row">
      <div class="col-12 col-md-6">
        <label for="firstName">{{form.firstName.label}}</label>
        <Input
            v-model="state.firstName"
            :warning="v$.firstName.$error"
            :placeholder="form.firstName.placeholder"
            name="firstName"
            id="firstName"
        />
        <span v-if="v$.firstName.$error" class="d-inline-block mt-1 text-secondary">{{ v$.firstName.$errors[0].$message }}</span>
      </div>
      <div class="col-12 col-md-6">
        <label>{{form.lastName.label}}</label>
        <Input
            v-model="state.lastName"
            :placeholder="form.lastName.placeholder"
            :warning="v$.lastName.$error"
        />
        <span v-if="v$.lastName.$error" class="d-inline-block mt-1 text-secondary">{{ v$.lastName.$errors[0].$message }}</span>
      </div>
      <div class="col-12 col-md-6">
        <label>{{form.email.label}}</label>
        <Input
            v-model="state.email"
            :placeholder="form.email.placeholder"
            :warning="v$.email.$error"
        />
        <span v-if="v$.email.$error" class="d-inline-block mt-1 text-secondary">{{ v$.email.$errors[0].$message }}</span>
      </div>
      <div class="col-12 col-md-6">
        <label>{{form.phoneNumber.label}}</label>
        <Input
            v-model="state.phone"
            :placeholder="form.phoneNumber.placeholder"
            :warning="v$.phone.$error"
        />
        <span v-if="v$.phone.$error" class="d-inline-block mt-1 text-secondary">{{ v$.phone.$errors[0].$message }}</span>
      </div>
      <div class="col-12 d-flex flex-column">
        <label>{{form.company.label}}</label>
        <Input
            v-model="state.company"
            :placeholder="form.company.placeholder"
            :warning="v$.company.$error"
        />
        <span v-if="v$.company.$error" class="d-inline-block mt-1 text-secondary">{{ v$.company.$errors[0].$message }}</span>
      </div>
      <div class="col-12 col-md-6">
        <label>{{form.city.label}}</label>
        <GMapAutocomplete
            :placeholder="form.city.placeholder"
            @place_changed="(location) => handleLocation(state, location, false, false, usaStates,canadaStates, false)"
            :class="{
                'item-danger': v$.city.$error,
                'active': state.isActive && !v$.city.$error
            }"
            class="input autocomplete-input"
            :options="state.autocompleteOptions"
            :value="state.city"
            @input="(e) => handleAutocomplete(state, e)"
            @focus="() => changeInputStatus(state)"
            @blur="() => changeInputStatus(state)"
        >
        </GMapAutocomplete>
        <span v-if="v$.city.$error" class="d-inline-block mt-1 text-secondary">{{ v$.city.$errors[0].$message }}</span>
      </div>
      <div class="col-12 col-md-6 form-modal__state-input">
        <label>{{form.state.label}}</label>
        <v-select
            :options="state.stateOptions"
            :selectable="o => !o.heading"
            class="select-standard"
            :searchable="false"
            :placeholder="form.state.placeholder"
            v-model="state.state">
          <template v-slot:option="option">
            <span v-if="option.heading" class="fw-bold">{{ option.heading }}</span>
            <span v-else class="ms-3">{{ option.label }}</span>
          </template>
        </v-select>
      </div>
      <div class="col-12 form-modal__inquiry-input">
        <label>{{form.inquiry.label}}</label>
        <v-select
          :options="state.inquiry"
          class="select-standard"
          :searchable="false"
          :placeholder="form.inquiry.placeholder"
          v-model="state.selectedInquiry"
        />
      </div>
      <div class="col-12 d-flex align-items-center my-3">
        <Checkbox v-model:checked="state.subscribeEmail" :id="getCheckboxId()" class="me-2 bg-white"/>
        <label :for="getCheckboxId()" class="user-select-none fw-normal form-label-mailing">{{form.checkbox}}</label>
      </div>
      <div class="col-12 mb-0">
        <ButtonRed
            :loading="isLoading"
            :disabled="isLoading"
            :text="form.button.text"
            class="w-100"
            @click="submitForm"
        />
      </div>
    </div>
  </form>
</template>

<script>
import Input from '@/components/common/Input'
import Select from "@/components/common/Select";
import Checkbox from "@/components/common/Checkbox";
import ButtonRed from "@/components/common/ButtonRed";
import {computed, reactive, watch} from "vue";
import { useVuelidate } from '@vuelidate/core'
import {bannerValidationOptions, formatPhoneNumber} from "@/utils/validators";
import {useReCaptcha} from "vue-recaptcha-v3";
import useLoadData from "@/composables/useLoadData";
import {
  handleLocation,
  handleSelected,
  changeInputStatus,
  handleAutocomplete
} from "@/utils/utils";
import {autocompleteOptions} from "@/config/config";
import submitFormToGa from "@/utils/submitFormToGa";

export default {
  components: {ButtonRed, Checkbox, Select, Input},
  props: {
    successFormMessage: {
      title: String,
      description: String
    },
    isModal: Boolean,
    form: {
      firstName: {
        label: String,
        placeholder: String
      },
      lastName: {
        label: String,
        placeholder: String
      },
      email: {
        label: String,
        placeholder: String
      },
      phoneNumber: {
        label: String,
        placeholder: String
      },
      company: {
        label: String,
        placeholder: String
      },
      city: {
        label: String,
        placeholder: String
      },
      state: {
        label: String,
        placeholder: String
      },
      inquiry: {
        label: String,
        placeholder: String,
        options: [String]
      },
      checkbox: String,
      button: {
        text: String
      },
      countries: [
        {
          name: String,
          states: [String]
        }
      ]
    },
  },
  data(){
    return {
      handleLocation,
      handleSelected,
      changeInputStatus,
      handleAutocomplete
    }
  },
  setup(props, {emit}){
    const { data: formSubmitResponse, fetchData: sendForm, error: submitError, isLoading } = useLoadData()
    const { executeRecaptcha, recaptchaLoaded } = useReCaptcha()
    const formName = props.form.name

    const source = props.form

    const usa = source.countries.filter(countryObject => countryObject.name === 'USA')[0];
    const usaStates = usa.states.map(elem => elem.name)
    const canada = source.countries.filter(countryObject => countryObject.name !== 'USA')[0];
    const canadaStates = canada.states.map(elem => elem.name)

    const state = reactive({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      testModel: '',
      company: '',
      state: '',
      stateOptions: [{ heading: usa.name },...usaStates, { heading: canada.name },...canadaStates],
      city: '',
      selectedInquiry: '',
      inquiry: props.form.inquiry.options,
      subscribeEmail: false,
      autocompleteOptions,
      showThanksMessage: false,
      isActive: false
    })

    const recaptcha = async (callback) => {
      isLoading.value = true;
      const data = {
        formName: formName,
        fields: [
          {name: 'firstName', value: state.firstName},
          {name: 'lastName', value: state.lastName},
          {name: 'email', value: state.email},
          {name: 'phone', value: state.phone},
          {name: 'company', value: state.company},
          {name: 'city', value: state.city},
          {name: 'state', value: state.state},
          {name: 'inquiry', value: state.selectedInquiry},
          {name: 'subscribeEmail', value: state.subscribeEmail},
        ],
      }

      // (optional) Wait until recaptcha has been loaded.
      await recaptchaLoaded()

      // Execute reCAPTCHA with action "login".
      const token = await executeRecaptcha('login')

      await sendForm({
        url: '/form-submit',
        method: 'post',
        data: {
          ...data,
          recaptchaToken: token
        }
      })

      submitFormToGa(formName);

      if(submitError.value === null) {
        state.showThanksMessage = true
      }

    }

    let rules = computed(() => bannerValidationOptions);

    const v$ = useVuelidate(rules, state)

    watch(() => state.phone, (newValue) => {
      console.log(newValue)
      state.phone = formatPhoneNumber(newValue)
    })

    return {
      v$,
      recaptcha,
      state,
      usaStates,
      canadaStates,
      isLoading
    }
  },
  methods:{
    getCheckboxId(){
      return this.$props.isModal ? 'formBannerModalCheckbox': 'formBannerCheckbox'
    },
    async submitForm(){
      await this.v$.$validate()

      if(!this.v$.$error && !this.isLoading) {
        this.recaptcha(() => {
          this.state.firstName = ''
          this.state.lastName = ''
          this.state.email = ''
          this.state.phone = ''
          this.state.company = ''
          this.state.city = ''
          this.state.state = ''
          this.state.selectedInquiry = ''
          this.state.subscribeEmail = false

          this.v$.firstName.$reset()
          this.v$.lastName.$reset()
          this.v$.email.$reset()
          this.v$.phone.$reset()
          this.v$.company.$reset()
          this.v$.city.$reset()
        });
      }
    }
  },
}
</script>
